import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { isEmpty, isEqual, isNull, size } from "lodash";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import { green, grey, red } from "@mui/material/colors";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtil from "@plannel/pages/utils/code-util";
import RpUtil from "@plannel/pages/replenishment-plan/utils/rp-util";
import planVersionService from "@plannel/services/data/plan-version-service";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";

const { versionStatusCode, module, limitVersion } = codeUtil;
const { RELEASED } = versionStatusCode;
const { RP } = module;
const REDUX_RP_VERSION_FILTER = "RP_VERSION_FILTER";

const RpVersionFilter = ({ viewName, rpVersionValue, onChangeFilter, statusFilters = [], useShipmentTxnOnly = false }) => {
  const { t } = useTranslation();

  const reduxDispatch = useDispatch();
  const rpVersionDataListReduxState = reduxUtil.getViewState(REDUX_RP_VERSION_FILTER);

  const [rpVersionDataList, setRpVersionDataList] = useState(rpVersionDataListReduxState?.rpVersion ? rpVersionDataListReduxState.rpVersion : []);
  const [rpVersionFilterDataList, setRpVersionFilterDataList] = useState([]);

  const onChangeHandler = (_, rpVersionValue) => {
    onChangeFilter(rpVersionValue);

    const createRpVersionReduxState = reduxUtil.createObjectState(viewName, { rpVersion: rpVersionValue });
    reduxDispatch(reduxUtil.updateViewState(createRpVersionReduxState));
  }

  const rpVersionDataStatusFilter = (results) => {
    const baseResults = useShipmentTxnOnly
      ? results.filter(result => result?.planConfigObj?.USE_SHIPMENT_TXN === true)
      : results;

    let resultsFilter = [];

    if(!isEmpty(statusFilters)) {
      for (const result of baseResults) {
        if (statusFilters.includes(result.statusCd)) {
          resultsFilter.push(result);
        }
      }
    } else {
      resultsFilter = baseResults;
    }

    /* 기억 하고있는 redux data가 제거되거나 변경되는경우 dropdown list에 데이터가 존재하는지 체크하는 변수 */
    const existSelectedValueInResults = resultsFilter.find(result => isEqual(result.id, rpVersionValue?.id));

    /* Default RP Version Settings */
    if (!isEmpty(resultsFilter)) {
      if (isEmpty(existSelectedValueInResults)) {
        onChangeHandler(null, resultsFilter[0]);
      } else {
        onChangeHandler(null, existSelectedValueInResults);
      }
    }
    
    return resultsFilter;
  }

  const sortRpVersions = versions => {
    const basicVersions = [];
    const executedVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd, execTs } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        if (isNull(execTs)) {
          basicVersions.push(ver);
        } else {
          executedVersions.push(ver);
        }
      }
    }
    const list = [...basicVersions, ...executedVersions, ...releaseVersions];
    if (size(list) > limitVersion) {
      const limitList = list.slice(0, limitVersion);
      return limitList;
    }
    return list;
  }

  
  const getRpVersionData = () => {
    /* 이전에 불러온 Dropdown data list가 존재하는경우 다시 가져오지 않음. */
    if (isEmpty(rpVersionDataListReduxState?.rpVersion)) {
      let rpVersionResultData = [];

      planVersionService.getAll({ moduleCd: RP, orderByColumn: "updatedTs", sortType: "desc" })
        .then(response => {
          const { results } = response.data;
          if(!isEmpty(results)) {
            rpVersionResultData = results;
          }

          const afterExecFilter = rpVersionResultData.filter(result => !isNull(result.execTs));

          const rpVersionFilterData = sortRpVersions(rpVersionDataStatusFilter(afterExecFilter));
          
          setRpVersionDataList(rpVersionResultData);
          setRpVersionFilterDataList(rpVersionFilterData);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const createRpVersionDataListReduxState = reduxUtil.createObjectState(REDUX_RP_VERSION_FILTER, { rpVersion: rpVersionDataList });
          reduxDispatch(reduxUtil.updateViewState(createRpVersionDataListReduxState));
        })
    } else {
      setRpVersionFilterDataList(sortRpVersions(rpVersionDataStatusFilter(rpVersionDataList)));
    }
  }

  const iconColor = planVersion => {
    if (planVersion) {
      if (isEqual(planVersion.statusCd, RELEASED)) {
        return grey[900];
      }

      return isEmpty(planVersion.execTs) ? red[600] : green[600];
    }

    return null;
  }

  useEffect(() => {
    getRpVersionData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <VersionAutocomplete
      options={rpVersionFilterDataList}
      value={rpVersionValue}
      onChange={onChangeHandler}
      label={t("version")}
      getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
      getIconColor={iconColor}
      getTooltip={RpUtil.getToolTip}
    />
  )
}

const reloadRpVersionFilter = (reduxDispatch) => {
  const rpVersionReduxFilterState = reduxUtil.getViewState(REDUX_RP_VERSION_FILTER);

  reduxDispatch(reduxUtil.removeViewState(rpVersionReduxFilterState));
}

export default React.memo(RpVersionFilter);
export { reloadRpVersionFilter };